<template>
  <div>
    <!-- MQTT Connection Status Banner -->
    <transition name="fade">
      <div v-if="mqttStatus !== 'connected'" class="mqtt-banner" :class="mqttStatus">
        <v-icon small class="mr-1">{{ mqttStatus === 'reconnecting' ? 'sync' : 'wifi_off' }}</v-icon>
        {{ mqttStatus === 'reconnecting' ? 'Reconnecting to live feed…' : 'Live feed disconnected' }}
        <span v-if="mqttStatus === 'stale'" class="stale-hint"> — data may be outdated</span>
      </div>
    </transition>

    <div class="style-row">
      <div style="width: 55%;">
        <LiveDiagram :mqttData="liveMqttData" :sources="diagramSources" :breakerConfig="breakerConfig" :mode="mode" />
      </div>
      <div style="width: 45%;">
        <KPICards :kpiData="kpiData" :invertersConfig="invertersConfig" :inverterData="inverterData" :mode="mode" />
      </div>
    </div>

    <div class="style-row add-margin">
      <!-- Main data card (LMS Data / ZES Data) -->
      <Card class="style-card">
        <div class="style-row header-row">
          <h3>{{ isLms ? 'LMS Power' : 'ZES Power' }}</h3>
          <div class="toggle-center">
            <Toggle class="mb-2" :modelValue="toggleValue" @toggleValueChange="(val) => toggleValue = val" :theme="theme">
              <v-btn flat value="graph">{{ $t('Graph') }}</v-btn>
              <v-btn flat value="table">{{ $t('Table') }}</v-btn>
            </Toggle>
          </div>
          <div class="download-group">
            <span v-if="activeFilter.Date" class="filter-date">{{ activeFilter.Date.from }}</span>
            <v-btn icon @click="downloadMainExcel" title="Download Excel" :disabled="!mainTableData.length">
              <v-icon>get_app</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Error state with retry -->
        <div v-if="mainError" class="error-state">
          <v-icon color="error" large>error_outline</v-icon>
          <span>Failed to load {{ isLms ? 'LMS' : 'ZES' }} data</span>
          <v-btn small outlined color="primary" @click="retryMain">Retry</v-btn>
        </div>

        <GlobalGraph v-else-if="toggleValue === 'graph'" :key="graphReload" class="graph"
          :series="loadComputedSeries" :xAxisData="loadXAxis" :yAxis="loadmfmYAxis"
          :theme="theme" :loading="mainLoading" />
        <DataTable v-else class="table-wrap" :headers="mainHeaders" :items="mainTableData" :isPaginationFalse="true" />
      </Card>

      <!-- MFM AC Power card -->
      <Card class="style-card">
        <div class="style-row header-row">
          <h3>MFM AC Power (kW)</h3>
          <div class="toggle-center">
            <Toggle class="mb-2" :modelValue="mfmToggleValue" @toggleValueChange="(val) => mfmToggleValue = val" :theme="theme">
              <v-btn flat value="graph">{{ $t('Graph') }}</v-btn>
              <v-btn flat value="table">{{ $t('Table') }}</v-btn>
            </Toggle>
          </div>
          <div class="download-group">
            <span v-if="activeFilter.Date" class="filter-date">{{ activeFilter.Date.from }}</span>
            <v-btn icon @click="downloadMfmExcel" title="Download Excel" :disabled="!mfmTableData.length">
              <v-icon>get_app</v-icon>
            </v-btn>
          </div>
        </div>

        <div v-if="mfmError" class="error-state">
          <v-icon color="error" large>error_outline</v-icon>
          <span>Failed to load MFM data</span>
          <v-btn small outlined color="primary" @click="retryMfm">Retry</v-btn>
        </div>

        <GlobalGraph v-else-if="mfmToggleValue === 'graph'" :key="graphReload" class="graph"
          :series="mfmDataComputedSeries" :xAxisData="mfmXAxis" :yAxis="mfmYAxis"
          :theme="theme" :loading="loading" />
        <DataTable v-else class="table-wrap" :headers="mfmHeaders" :items="mfmTableData" :isPaginationFalse="true" />
      </Card>
    </div>
  </div>
</template>

<script>
import LiveDiagram from '../components/zes/LiveDiagram.vue';
import KPICards from '../components/zes/KPICards.vue';
import GlobalGraph from '@/components/common/GlobalGraph';
import loadApi from "@/services/loadManager.js";
const momentTz = require('moment-timezone');
import { mapGetters } from 'vuex';
import { mapGettersMixin } from '@/util/mixin/index';
import ExcelJS from "exceljs";
import _ from 'lodash';

// How long without an MQTT message before we mark data as stale (ms)
const MQTT_TTL_MS = 60 * 1000;

export default {
  props: {
    mode: {
      type: String,
      required: true,
      validator: v => ['lms', 'zes'].includes(v)
    },
    changesFromFilter: {
      type: Object,
      default: () => ({})
    },
  },

  mixins: [mapGettersMixin],
  components: { LiveDiagram, KPICards },

  async created() {
    this.setIntervalWithoutDelay(this.callAllAPIs);
  },

  beforeDestroy() {
    this.$mqtt.unsubscribe(`${this.mode}/live/${this.getSelectedPlantId}`);
    if (this._mqttTTLTimer) clearTimeout(this._mqttTTLTimer);
    // Remove bound handlers to avoid listener leaks on hot-reload
    this.$mqtt.off('message', this._onMqttMessage);
    this.$mqtt.off('error', this._onMqttError);
    this.$mqtt.off('offline', this._onMqttOffline);
    this.$mqtt.off('reconnect', this._onMqttReconnect);
  },

  data() {
    return {
      graphReload: 0,
      // MFM graph
      mfmGraphData: [],
      loading: false,
      mfmError: false,
      mfmXAxis: [],
      mfmYAxis: [{
        type: 'value',
        name: 'AC Power (kW)',
        nameLocation: 'middle',
        nameGap: 55,
        nameTextStyle: { fontSize: 12, fontWeight: 600 },
        axisLine: {
          show: true
        },
        axisTick: { show: true },
      }],

      loadmfmYAxis: [{
        type: 'value',
        name: 'AC Power (kW)',
        nameLocation: 'middle',
        nameGap: 55,
        nameTextStyle: { fontSize: 12, fontWeight: 600 },
        axisLine: {
          show: true
        },
        axisTick: { show: true },
      }],
      // Main data card
      loadData: [],
      loadXAxis: [],
      mainLoading: false,
      mainError: false,
      // Toggles
      toggleValue: 'graph',
      mfmToggleValue: 'graph',
      // State
      activeFilter: {},
      plantConfig: {},
      mfmConfig: {},
      solarEnergyRef: '',
      totalSolarEnergy: 0,
      liveMqttData: {},
      _mqttTTLTimer: null,
      mqttStatus: 'connected', // 'connected' | 'reconnecting' | 'offline' | 'stale'
      kpiData: {
        activeDg: null,
        runtime: null,
        dgEnergyToday: null,
        dcCapacity: null,
        activeInverters: null,
        energyTodaySolar: null,
      },
      invertersConfig: [],
      inverterData: {},
      breakerConfig: null,
      isInitialLoad: true,
    };
  },

  computed: {
    ...mapGetters(['getSelectedPlantId', 'getGeneralState', 'getActivePlantTimezone']),

    isLms() { return this.mode === 'lms'; },

    filter() {
      return [{ id: "Date", type: "singleDatePicker", title: "Date" }];
    },

    diagramSources() {
      if (this.isLms) return { grid: !!(this.mfmConfig && this.mfmConfig.og), dg: true, spv: true };
      return { grid: true, dg: false, spv: true };
    },

    mfmDataComputedSeries() {
      return _.cloneDeep(this.mfmGraphData).map(el => { el.name = this.$t(el.name); return el; });
    },

    loadComputedSeries() {
      return _.cloneDeep(this.loadData).map(el => { el.name = this.$t(el.name); return el; });
    },

    mainHeaders() {
      return [
        { text: 'Date', value: 'date', align: 'center' },
        ...this.invertersConfig.map(inv => ({
          text: inv.name,
          value: `inverter-${inv.deviceId}`,
          align: 'center'
        }))
      ];
    },

    mainTableData() {
      if (!this.loadXAxis.length) return [];
      return this.loadXAxis.map((value, index) => {
        const row = { date: value };
        this.loadData.forEach(series => {
          row[series.id] = series.data[index] != null ? series.data[index] : null;
        });
        return row;
      });
    },

    mfmHeaders() {
      return [
        { text: 'Time', value: 'time', align: 'center' },
        ...this.mfmGraphData.map(obj => ({ text: obj.name, value: obj.name, align: 'center' }))
      ];
    },

    mfmTableData() {
      if (!this.mfmXAxis.length) return [];
      return this.mfmXAxis.map((value, index) => {
        const row = { time: value };
        this.mfmGraphData.forEach(ser => {
          const val = ser.data[index];
          row[ser.name] = val != null ? val : null;
        });
        return row;
      });
    },
  },

  methods: {
    // ─── Orchestration ───────────────────────────────────────────────────────

    async callAllAPIs() {
      this.initialStates();
      await this.initialConfigList();
      // Config must be sequential; subsequent calls are parallelised where safe
      await this.getPlantConfig();
      await this.getInverterConfig();
      // Graph data, main data and solar energy can run in parallel
      await Promise.allSettled([
        this.getGraphData(),
        this.getMainData(),
        this.getSolarEnergy(),
      ]);
      this.mqttHandler();
    },

    initialStates() {
      this.plantId = this.getSelectedPlantId;
      this.tempData = '';
      this.totalSolarEnergy = 0;
      this.mainError = false;
      this.mfmError = false;

      this.mfmGraphData = [];
      this.mfmXAxis = [];
      this.loadData = [];
      this.loadXAxis = [];
      this.inverterData = {};
      this.invertersConfig = [];
      this.liveMqttData = {};
      this.kpiData = {
        activeDg: null,
        runtime: null,
        dgEnergyToday: null,
        dcCapacity: null,
        activeInverters: null,
        energyTodaySolar: null,
      };
    },

    async initialConfigList() {
      const today = momentTz().tz(this.getActivePlantTimezone).format('YYYY-MM-DD');
      const cloneFilterOption = _.cloneDeep(this.filter);
      this.activeFilter = cloneFilterOption.reduce((acc, child) => {
        const conditionMet = !child.condition || (child.considerStateAlways || child.condition(acc[child.ref]));
        if (conditionMet) acc[child.id] = { from: today, to: today };
        return acc;
      }, {});
      this.$emit('filter:config', cloneFilterOption);
      this.$emit('initial:active_filter', this.activeFilter);
    },

    // ─── Config loaders ───────────────────────────────────────────────────────

    async getPlantConfig() {
      const response = await loadApi.getPlantConfig(this.plantId);
      this.plantConfig = response.data;
      const result = this.plantConfig[0].zes_lms_config || {};
      this.mfmConfig = result['mfm_config'];
      this.breakerConfig = result['breaker_config'] || null;
      this.solarEnergyRef = result['solar_energy'] && result['solar_energy']['src'] ? result['solar_energy']['src'] : 'inverter';
    },

    async getInverterConfig() {
      this.invertersConfig = [];
      this.inverterData = {};
      try {
        const response = await loadApi.getInverterConfig({ plantId: this.plantId });
        let totalDcCapacity = 0;
        let inverterCount = 0;
        response.data.result.forEach(inv => {
          this.invertersConfig.push({
            name: inv.name_config,
            dcCapacity: inv.inverter_dc_capacity || 0,
            deviceId: inv.device_id
          });
          inverterCount++;
          this.inverterData[`inverter-${inv.device_id}`] = { liveValue: null, setValue: null };
          totalDcCapacity += parseFloat(inv.inverter_dc_capacity || 0);
        });
        this.kpiData.dcCapacity = totalDcCapacity.toFixed(2);
      } catch (err) {
        console.error('❌ Error fetching inverter config:', err);
      }
    },

    // ─── Data loaders ─────────────────────────────────────────────────────────

    async getGraphData() {
      this.loading = true;
      this.mfmError = false;
      try {
        if (this.mfmConfig) {
          const response = await loadApi.getMfmData({
            plantId: this.plantId,
            date: this.activeFilter.Date.from,
            query: Object.values(this.mfmConfig).flat(),
            parameters: [{ label: 'AC Power(kW)', parameter: 'ac_power' }]
          });
          const data = response.data.message;
          if (this.isLms) {
            const dgRuntime = Math.max(0, ...data.graph
              .filter(item => this.mfmConfig.dg.includes(item.deviceId))
              .map(item => item.count)
            );
            this.kpiData.runtime = dgRuntime > 0 ? dgRuntime : null;
          }
          this.mfmGraphData = data.graph;
          this.mfmXAxis = data.xAxis;
        } else {
          this.mfmError = true;
        }
      } catch (err) {
        console.error('❌ Error fetching MFM data:', err);
        this.mfmError = true;
      } finally {
        this.loading = false;
      }
    },

    async getMainData() {
      this.loadData = [];
      this.loadXAxis = [];
      this.mainLoading = true;
      this.mainError = false;
      try {
        const date = this.activeFilter.Date.from;
        const response = await loadApi.getLoadData(this.plantId, date, this.mode);
        const records = response.data.records;
        if (!records || !records.length) return;

        this.loadXAxis = records.map(r =>
          momentTz.unix(r.sensor_time).tz(this.getActivePlantTimezone).format('HH:mm:ss')
        );

        const deviceKeys = Object.keys(records[0]).filter(k => k.startsWith('inverter-'));

        const idNameMap = this.invertersConfig.reduce((acc, inv) => {
          acc[`inverter-${inv.deviceId}`] = inv.name;
          return acc;
        }, {});

        this.loadData = deviceKeys.map(deviceKey => ({
          id: deviceKey,
          name: idNameMap[deviceKey] || deviceKey,
          type: 'line',
          smooth: true,
          data: records.map(r => r[deviceKey] ? r[deviceKey].set_ac_power : null)
        }));
      } catch (err) {
        console.error(`❌ Error fetching ${this.mode} data:`, err);
        this.mainError = true;
      } finally {
        this.mainLoading = false;
      }
    },

    // ─── Retry helpers ────────────────────────────────────────────────────────

    async retryMain() {
      await this.getMainData();
    },

    async retryMfm() {
      await this.getGraphData();
    },

    // ─── Solar energy ─────────────────────────────────────────────────────────

    _sumInvGeneration(result) {
      return result.reduce((sum, inv) => sum + parseFloat(inv.gen || 0), 0);
    },

    async getSolarEnergy() {
      try {
        if (this.isLms && this.mfmConfig) {
          let dgEnergyExport = 0;
          const defaultParams = ['active_total_export_cumulative', 'active_total_import_cumulative', 'ac_power'].join(',');
          const idParamMap = {};
          Object.values(this.mfmConfig).forEach(arr => {
            arr.forEach(param => { idParamMap[param] = defaultParams; });
          });
          const blocks = new Set(this.mfmConfig.dg.map(id => id.split('-')[0]));
          await Promise.all([...blocks].map(async (blockId) => {
            const response = await loadApi.getMfmList({
              plantId: this.plantId, blockId, date: this.activeFilter.Date.from, idParamMap
            });
            response.data.message.forEach(mfm => {
              const key = `${mfm.block_id}-${mfm.feeder_id}`;
              if (this.mfmConfig.dg.includes(key)) dgEnergyExport += parseFloat(mfm.export || 0);
              if (this.solarEnergyRef === 'mfm' && this.mfmConfig.inverter.includes(key))
                this.totalSolarEnergy += parseFloat(mfm.export || 0);
            });
          }));
          this.kpiData.dgEnergyToday = dgEnergyExport > 0 ? dgEnergyExport.toFixed(2) : null;
          if (this.solarEnergyRef !== 'inverter') {
            this.kpiData.energyTodaySolar = this.totalSolarEnergy.toFixed(2);
            return;
          }
        }
        const invResponse = await loadApi.getTotalInvGeneration(this.plantId, this.activeFilter.Date.from);
        const solarTotal = this._sumInvGeneration(invResponse.data.result);
        this.kpiData.energyTodaySolar = solarTotal > 0 ? solarTotal.toFixed(2) : null;
      } catch (err) {
        console.error('❌ Error fetching solar energy:', err);
      }
    },

    // ─── MQTT ─────────────────────────────────────────────────────────────────

    _transformMqttData(raw) {
      if (!raw || !Object.keys(raw).length) {
        return this.isLms
          ? { gridPower: 0, dgPower: 0, solarPower: 0, totalPower: 0 }
          : { gridPower: 0, solarPower: 0, totalPower: 0 };
      }
      const acPowerSet = Object.keys(raw)
        .filter(k => k.startsWith('inverter-'))
        .reduce((sum, k) => sum + ((raw[k] && raw[k].set_ac_power) || 0), 0);
      const ogMfm = raw.og || {};
      const load = raw.load || {};
      const solarPower = raw.plant_level && raw.plant_level.ac_power != null ? raw.plant_level.ac_power : null;
      const finalObject = {
        gridPower: ogMfm.total != null ? ogMfm.total : null,
        solarPower,
        totalPower: load.total != null ? load.total : null,
        isLive: true
      };
      if (this.isLms) finalObject.dgPower = (raw.dg && raw.dg.total) || 0;
      if (acPowerSet > 0) finalObject.acPowerSet = acPowerSet;
      if (Object.keys(raw).some(k => k.startsWith('breaker-'))) {
        finalObject.breakers = Object.fromEntries(
          Object.keys(raw).filter(k => k.startsWith('breaker-'))
            .map(k => [k, raw[k].status === 1 ? 'on' : 'off'])
        );
      }
      return finalObject;
    },

    buildInverterData(raw) {
      const inverterData = {};
      Object.keys(raw).filter(k => k.startsWith('inverter-')).forEach(k => {
        inverterData[k] = {
          liveValue: (raw[k] && raw[k].ac_power) || 0,
          setValue: (raw[k] && raw[k].set_ac_power) || 0
        };
      });
      return inverterData;
    },

    mqttHandler() {
      this.$mqtt.subscribe(`${this.mode}/live/${this.getSelectedPlantId}`);
      if (this.mqttBound) return;
      this.mqttBound = true;

      // Store as named methods so we can remove them in beforeDestroy
      this._onMqttMessage = (topic, message) => {
        const parsed = JSON.parse(message.toString());

        const currentTime = Math.floor(Date.now() / 1000);
        const sensorTime = parseInt(parsed.sensor_time, 10);

        if (!sensorTime || isNaN(sensorTime) || (currentTime - sensorTime) > 60) {
          console.warn('⏱️ Ignoring stale MQTT packet:', parsed.sensor_time);
          return;
        }

        this.liveMqttData = this._transformMqttData(parsed);
        this.inverterData = this.buildInverterData(parsed);
        this.mqttStatus = 'connected';

        let activeInverterCount = 0;

        Object.entries(parsed).forEach(([key, value]) => {
          if (key.startsWith('inverter-') && value.ac_power > 0) activeInverterCount++;
        });
        this.kpiData.activeInverters = `${activeInverterCount}/${this.invertersConfig.length}`;

        if (this.isLms) {
          let activeDgCount = 0;
          Object.entries(parsed.dg || {}).forEach(([key, value]) => {
            if (key.startsWith('mfm-') && value.ac_power > 0) activeDgCount++;
          });
          this.kpiData.activeDg = `${activeDgCount}/${this.mfmConfig.dg.length}`;
        }

        if (this._mqttTTLTimer) clearTimeout(this._mqttTTLTimer);
        this._mqttTTLTimer = setTimeout(() => {
          console.warn('⏱️ MQTT TTL expired — data is stale');
          this.mqttStatus = 'stale';
          this.liveMqttData = this.isLms
            ? { gridPower: 0, dgPower: 0, solarPower: 0, totalPower: 0 }
            : { gridPower: 0, solarPower: 0, totalPower: 0 };
          this.inverterData = Object.fromEntries(
            Object.keys(this.inverterData).map(k => [k, { liveValue: 0, setValue: 0 }])
          );
          this.kpiData.activeInverters = `0/${this.invertersConfig.length}`
          if (this.isLms) this.kpiData.activeDg = `0/${this.mfmConfig.dg.length}`;
        }, MQTT_TTL_MS);
      };

      this._onMqttError = err => { console.error('MQTT ERROR:', err); this.mqttStatus = 'offline'; };
      this._onMqttOffline = () => { console.warn('MQTT offline'); this.mqttStatus = 'offline'; };
      this._onMqttReconnect = () => { console.log('MQTT reconnect'); this.mqttStatus = 'reconnecting'; };

      this.$mqtt.on('message', this._onMqttMessage);
      this.$mqtt.on('error', this._onMqttError);
      this.$mqtt.on('offline', this._onMqttOffline);
      this.$mqtt.on('reconnect', this._onMqttReconnect);
    },

    // ─── Excel export ─────────────────────────────────────────────────────────

    async downloadMainExcel() {
      if (!this.mainTableData.length) return;
      const label = this.isLms ? 'LMS Power' : 'ZES Power';
      const fileName = this.isLms ? 'LMS_Data' : 'ZES_Data';
      await this._writeExcel(label, fileName, this.mainHeaders, this.mainTableData);
    },

    async downloadMfmExcel() {
      if (!this.mfmTableData.length) return;
      await this._writeExcel('MFM AC Power (kW)', 'MFM_AC_Power', this.mfmHeaders, this.mfmTableData);
    },

    async _writeExcel(title, filePrefix, headers, rows) {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(title);
      worksheet.views = [{ showGridLines: false }];

      const titleRow = worksheet.getRow(1);
      titleRow.values = [title];
      titleRow.getCell(1).font = { size: 16, bold: true };
      worksheet.addRow(['Date:', this.activeFilter.Date.from]);
      worksheet.addRow([]);

      const headerRow = worksheet.addRow(headers.map(h => h.text));
      headerRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000427' } };
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        cell.alignment = { horizontal: 'center' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      rows.forEach(row => {
        const values = headers.map(h => row[h.value] !== undefined && row[h.value] !== null ? row[h.value] : '');
        const r = worksheet.addRow(values);
        r.eachCell(cell => {
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          cell.alignment = { horizontal: 'center' };
        });
      });
      worksheet.columns = headers.map(() => ({ width: 18 }));

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${filePrefix}_${this.activeFilter.Date.from}.xlsx`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    },
  },

  watch: {
    // Debounced to avoid firing for every rapid filter keystroke
    changesFromFilter: {
      handler: _.debounce(async function (val) {
        if (this.isInitialLoad) { this.isInitialLoad = false; return; }
        this.activeFilter = val;
        await Promise.allSettled([
          this.getGraphData(),
          this.getMainData(),
          this.getSolarEnergy(),
        ]);
      }, 300),
      deep: true,
    },

    async getSelectedPlantId(newVal, oldVal) {
      this.isInitialLoad = true;

      // ✅ Fully tear down old MQTT before rebinding
      this.$mqtt.unsubscribe(`${this.mode}/live/${oldVal}`);
      this.$mqtt.off('message', this._onMqttMessage);
      this.$mqtt.off('error', this._onMqttError);       // was missing
      this.$mqtt.off('offline', this._onMqttOffline);   // was missing
      this.$mqtt.off('reconnect', this._onMqttReconnect); // was missing
      if (this._mqttTTLTimer) clearTimeout(this._mqttTTLTimer);
      this.mqttStatus = 'connected';
      this.mqttBound = false;

      await this.setIntervalWithoutDelay(this.callAllAPIs);
    },

    breakerConfig: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          if (!this.$refs.svgContainer) return;
          this.$refs.svgContainer.innerHTML = '';
          this.createSVG();
          this.$nextTick(() => { this.animateMovers(); });
        });
      }
    },

    mode(newMode, oldMode) {
      // Unsubscribe old mode topic before rebinding
      this.$mqtt.unsubscribe(`${oldMode}/live/${this.getSelectedPlantId}`);
      this.$mqtt.off('message', this._onMqttMessage);
      this.$mqtt.off('error', this._onMqttError);
      this.$mqtt.off('offline', this._onMqttOffline);
      this.$mqtt.off('reconnect', this._onMqttReconnect);
      this.mqttBound = false;

      if (this._mqttTTLTimer) clearTimeout(this._mqttTTLTimer);
      this.mqttStatus = 'connected';

      this.setIntervalWithoutDelay(this.callAllAPIs);
    },
  }
};
</script>

<style scoped>
.graph {
  width: 100%;
  height: calc(100vh - 45vh);
}

.style-card {
  box-shadow: 0px 5px 5px 0px #334079;
  border-radius: 8px;
  width: 50%;
  padding: 12px;
}

.style-row {
  display: flex;
  gap: 8px;
}

.add-margin {
  margin-top: 8px;
}

.table-wrap {
  width: 100%;
  margin-top: 6px;
  border-radius: 10px;
  border: 1px solid #1e2f5e;
  max-height: calc(100vh - 50vh);
  overflow-y: auto;
  overflow-x: hidden;
}

.table-wrap::-webkit-scrollbar { width: 6px; }
.table-wrap::-webkit-scrollbar-track { background: transparent; }
.table-wrap::-webkit-scrollbar-thumb { background: #334079; border-radius: 4px; }
.table-wrap::-webkit-scrollbar-thumb:hover { background: #668CF6; }

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  margin-top: 4px;
}

.toggle-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 4px;
}

/* ── MQTT status banner ───────────────────────────── */
.mqtt-banner {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 6px;
  margin-bottom: 8px;
}

.mqtt-banner.offline,
.mqtt-banner.stale {
  background: rgba(255, 82, 82, 0.12);
  color: #EF5350;
  border: 1px solid rgba(255, 82, 82, 0.25);
}

.mqtt-banner.reconnecting {
  background: rgba(255, 183, 77, 0.12);
  color: #FFB74D;
  border: 1px solid rgba(255, 183, 77, 0.25);
}

.stale-hint { opacity: 0.7; }

/* ── Error state ─────────────────────────────────── */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 160px;
  color: var(--v-primaryText-base);
  opacity: 0.8;
  font-size: 14px;
}

.download-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filter-date {
  font-size: 12px;
  font-weight: 600;
  opacity: 0.75;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

/* ── Transitions ─────────────────────────────────── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>
